import * as THREE from 'three';

/**
 * MultiRoomManager - Manages multiple showroom containers for optimized loading
 * 
 * This class handles the isolation and management of multiple loaded showrooms
 * to enable instant room switching without rebuilding scenes.
 * 
 * Key Features:
 * - Creates isolated containers for each showroom
 * - Manages visibility switching between rooms
 * - Maintains backward compatibility with existing single-room workflow
 * - Tracks room loading states and readiness
 */
export class MultiRoomManager {
  constructor(scene) {
    this.scene = scene;
    this.roomContainers = new Map(); // roomId -> THREE.Group
    this.roomStates = new Map(); // roomId -> { loaded, loading, error }
    this.currentRoomId = null;
    
    // Backward compatibility: if disabled, works exactly like before
    this.multiRoomEnabled = true; // Feature flag for gradual rollout
    
    console.log('🏗️ MultiRoomManager initialized');
  }

  /**
   * Create an isolated container for a specific room
   * @param {string|number} roomId - Unique identifier for the room
   * @returns {THREE.Group} The container for this room's assets
   */
  createRoomContainer(roomId) {
    // Creating container for room
    
    // Check if container already exists
    if (this.roomContainers.has(roomId)) {
      console.log(`⚠️ Container for room ${roomId} already exists, returning existing`);
      return this.roomContainers.get(roomId);
    }

    // Create new isolated container
    const container = new THREE.Group();
    container.name = `room_container_${roomId}`;
    container.userData = {
      roomId: roomId,
      createdAt: Date.now(),
      type: 'showroom_container'
    };

    // Start hidden - will be made visible when switched to
    container.visible = false;

    // Add to scene and track
    this.scene.add(container);
    this.roomContainers.set(roomId, container);
    
    // Initialize room state
    this.roomStates.set(roomId, {
      loaded: false,
      loading: false,
      error: null,
      containerCreated: true
    });

    // Container created
    return container;
  }

  /**
   * Get the container for a specific room (creates if doesn't exist)
   * @param {string|number} roomId - Room identifier
   * @returns {THREE.Group} The room's container
   */
  getRoomContainer(roomId) {
    if (!this.multiRoomEnabled) {
      // Backward compatibility: return scene directly
      return this.scene;
    }

    let container = this.roomContainers.get(roomId);
    if (!container) {
      container = this.createRoomContainer(roomId);
    }
    return container;
  }

  /**
   * Switch to display a specific room (instant if loaded)
   * @param {string|number} roomId - Target room ID
   * @param {Function} beforeRevealCallback - Callback to execute while screen is black
   * @returns {boolean} True if switch was successful
   */
  switchToRoom(roomId, beforeRevealCallback = null) {
    // Switching rooms

    if (!this.multiRoomEnabled) {
      console.log('📋 Multi-room disabled, using traditional switching');
      return true; // Let existing system handle it
    }

    // Check if target room container exists
    const targetContainer = this.roomContainers.get(roomId);
    if (!targetContainer) {
      console.warn(`❌ Room ${roomId} container not found, cannot switch`);
      return false;
    }

    // If switching to same room, do nothing
    if (this.currentRoomId === roomId) {
      return true;
    }

    // Smooth transition between rooms
    this.performRoomTransition(this.currentRoomId, roomId, beforeRevealCallback);
    return true;
  }

  /**
   * Perform smooth transition between rooms with screen fade
   * @param {string|number} fromRoomId - Current room ID (can be null)
   * @param {string|number} toRoomId - Target room ID
   * @param {Function} beforeRevealCallback - Callback to execute while screen is black
   */
  async performRoomTransition(fromRoomId, toRoomId, beforeRevealCallback = null) {
    const targetContainer = this.roomContainers.get(toRoomId);
    const currentContainer = fromRoomId ? this.roomContainers.get(fromRoomId) : null;

    // Immediate switch for first room or if no animation needed
    if (!fromRoomId || !currentContainer) {
      targetContainer.visible = true;
      this.currentRoomId = toRoomId;
      return;
    }

    // Create black overlay instantly to cover any blinking
    const fadeOverlay = this.createFadeOverlay();
    fadeOverlay.style.opacity = '1'; // Start black immediately
    
    // Switch rooms while screen is black
    currentContainer.visible = false;
    targetContainer.visible = true;
    this.currentRoomId = toRoomId;
    
    // Execute callback (camera positioning) while screen is black
    if (beforeRevealCallback) {
      beforeRevealCallback();
    }
    
    // Small delay to ensure room is rendered AND positioned
    await new Promise(resolve => setTimeout(resolve, 150));
    
    // Fade from black to reveal new room
    await this.fadeScreen(fadeOverlay, 1, 0, 200);
    
    // Remove overlay
    this.removeFadeOverlay(fadeOverlay);
  }

  /**
   * Create screen fade overlay
   */
  createFadeOverlay() {
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100vw';
    overlay.style.height = '100vh';
    overlay.style.backgroundColor = 'black';
    overlay.style.opacity = '0';
    overlay.style.pointerEvents = 'none';
    overlay.style.zIndex = '9999';
    overlay.style.transition = 'none';
    
    document.body.appendChild(overlay);
    return overlay;
  }

  /**
   * Animate screen fade
   */
  async fadeScreen(overlay, fromOpacity, toOpacity, duration) {
    return new Promise((resolve) => {
      const startTime = performance.now();
      
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        const currentOpacity = fromOpacity + (toOpacity - fromOpacity) * progress;
        overlay.style.opacity = currentOpacity.toString();
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };
      
      requestAnimationFrame(animate);
    });
  }

  /**
   * Remove fade overlay
   */
  removeFadeOverlay(overlay) {
    if (overlay && overlay.parentNode) {
      overlay.parentNode.removeChild(overlay);
    }
  }

  /**
   * Check if a room is loaded and ready for instant switching
   * @param {string|number} roomId - Room identifier
   * @returns {boolean} True if room is ready
   */
  isRoomReady(roomId) {
    const state = this.roomStates.get(roomId);
    return state ? (state.loaded && !state.loading && !state.error) : false;
  }

  /**
   * Update the loading state of a room
   * @param {string|number} roomId - Room identifier
   * @param {Object} stateUpdate - State properties to update
   */
  updateRoomState(roomId, stateUpdate) {
    const currentState = this.roomStates.get(roomId) || {};
    const newState = { ...currentState, ...stateUpdate };
    this.roomStates.set(roomId, newState);
    
    // Room state updated silently
  }

  /**
   * Mark a room as fully loaded
   * @param {string|number} roomId - Room identifier
   */
  markRoomAsLoaded(roomId) {
    this.updateRoomState(roomId, {
      loaded: true,
      loading: false,
      error: null,
      loadedAt: Date.now()
    });
  }

  /**
   * Mark a room as currently loading
   * @param {string|number} roomId - Room identifier
   */
  markRoomAsLoading(roomId) {
    this.updateRoomState(roomId, {
      loaded: false,
      loading: true,
      error: null,
      loadStartedAt: Date.now()
    });
  }

  /**
   * Mark a room as failed to load
   * @param {string|number} roomId - Room identifier
   * @param {Error|string} error - Error details
   */
  markRoomAsError(roomId, error) {
    this.updateRoomState(roomId, {
      loaded: false,
      loading: false,
      error: error.message || error,
      errorAt: Date.now()
    });
  }

  /**
   * Get the current active room ID
   * @returns {string|number|null} Current room ID
   */
  getCurrentRoomId() {
    return this.currentRoomId;
  }

  /**
   * Get the current active room container
   * @returns {THREE.Group|null} Current room container
   */
  getCurrentRoomContainer() {
    if (!this.currentRoomId) return null;
    return this.roomContainers.get(this.currentRoomId);
  }

  /**
   * Get all interactive objects from the current visible room only
   * This ensures raycasting only works on visible room objects
   * @returns {THREE.Object3D[]} Array of interactive objects in visible room
   */
  getCurrentRoomInteractiveObjects() {
    const currentContainer = this.getCurrentRoomContainer();
    if (!currentContainer) return [];

    const interactiveObjects = [];
    
    // Recursively find all meshes in current room container
    currentContainer.traverse((child) => {
      if (child.isMesh || (child.userData && child.userData.interactive)) {
        interactiveObjects.push(child);
      }
    });

    return interactiveObjects;
  }

  /**
   * Get all floor meshes from the current visible room only
   * @returns {THREE.Mesh[]} Array of floor meshes in visible room
   */
  getCurrentRoomFloorMeshes() {
    const currentContainer = this.getCurrentRoomContainer();
    if (!currentContainer) {
      console.warn('⚠️ No current room container found for floor mesh detection');
      return [];
    }

    const floorMeshes = [];
    
    currentContainer.traverse((child) => {
      if (child.isMesh && (child.name === 'floor' || child.name.includes('floor'))) {
        floorMeshes.push(child);
        // Found floor mesh
      }
    });

    // Found floor meshes in visible container
    return floorMeshes;
  }

  /**
   * Get loading progress for all rooms
   * @returns {Object} Progress information
   */
  getLoadingProgress() {
    const totalRooms = this.roomStates.size;
    let loadedRooms = 0;
    let loadingRooms = 0;
    let errorRooms = 0;

    for (const state of this.roomStates.values()) {
      if (state.loaded) loadedRooms++;
      else if (state.loading) loadingRooms++;
      else if (state.error) errorRooms++;
    }

    return {
      total: totalRooms,
      loaded: loadedRooms,
      loading: loadingRooms,
      errors: errorRooms,
      progress: totalRooms > 0 ? (loadedRooms / totalRooms) : 0
    };
  }

  /**
   * Get detailed state information for all rooms
   * @returns {Map} Room states map
   */
  getAllRoomStates() {
    return new Map(this.roomStates);
  }

  /**
   * Enable or disable multi-room functionality
   * @param {boolean} enabled - Whether to use multi-room system
   */
  setMultiRoomEnabled(enabled) {
    console.log(`🔧 Multi-room functionality ${enabled ? 'enabled' : 'disabled'}`);
    this.multiRoomEnabled = enabled;
  }

  /**
   * Clean up a specific room (remove from memory)
   * Use sparingly - this defeats the purpose of keeping rooms loaded
   * @param {string|number} roomId - Room to clean up
   */
  cleanupRoom(roomId) {
    console.log(`🧹 Cleaning up room ${roomId}`);
    
    const container = this.roomContainers.get(roomId);
    if (container) {
      // Remove from scene
      this.scene.remove(container);
      
      // Dispose all objects in container
      container.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      });

      // Remove from tracking
      this.roomContainers.delete(roomId);
      this.roomStates.delete(roomId);
      
      // Update current room if this was it
      if (this.currentRoomId === roomId) {
        this.currentRoomId = null;
      }
    }
  }

  /**
   * Clean up all rooms and reset manager
   * Fallback for memory pressure situations
   */
  cleanupAllRooms() {
    console.log('🧹 Cleaning up all rooms');
    
    for (const roomId of this.roomContainers.keys()) {
      this.cleanupRoom(roomId);
    }
    
    this.currentRoomId = null;
    console.log('✅ All rooms cleaned up');
  }

  /**
   * Get memory usage statistics
   * @returns {Object} Memory and performance info
   */
  getMemoryInfo() {
    const containers = this.roomContainers.size;
    let totalObjects = 0;
    let visibleObjects = 0;

    for (const container of this.roomContainers.values()) {
      container.traverse(() => totalObjects++);
      if (container.visible) {
        container.traverse(() => visibleObjects++);
      }
    }

    return {
      containers,
      totalObjects,
      visibleObjects,
      currentRoom: this.currentRoomId,
      multiRoomEnabled: this.multiRoomEnabled
    };
  }
}